import { ASTQuery } from "@malloydata/malloy-query-builder";
import {
  queryToMalloy,
  type Query,
  type Expression,
  type ModelInfo,
} from "@malloydata/malloy-interfaces";
import type {
  QueryDefinition,
  SourceDefinition,
  VisualizationConfig,
  VisualizationType,
} from "./types";
export interface GuidedSpec {
  source: string;
  measures: string[];
  groups: string[];
  granularity: "day" | "week" | "month";
  filters: { field: string; value: string }[];
  order: string;
  direction: "asc" | "desc";
  limit: number;
}
const reference = (name: string) => {
  const path = name.split(".");
  return { name: path.pop()!, ...(path.length ? { path } : {}) };
};
export const alias = (name: string) => name.replaceAll(".", "_");
export function simpleQuery(
  source: string,
  measures: string[],
  groups: string[] = [],
): Query {
  return {
    definition: {
      kind: "arrow",
      source: { kind: "source_reference", name: source },
      view: {
        kind: "segment",
        operations: [
          ...groups.map((name) => ({
            kind: "group_by" as const,
            field: {
              expression: {
                kind: "field_reference" as const,
                ...reference(name),
              },
            },
            name: alias(name),
          })),
          ...measures.map((name) => ({
            kind: "aggregate" as const,
            field: {
              expression: {
                kind: "field_reference" as const,
                ...reference(name),
              },
            },
            name: alias(name),
          })),
        ],
      },
    },
  };
}
export function reportText(report: QueryDefinition): string {
  return report.mode === "guided" ? queryToMalloy(report.query) : report.malloy;
}
const expressionName = (expr: Expression): string =>
  expr.kind === "field_reference"
    ? [...(expr.path ?? []), expr.name].join(".")
    : expr.kind === "time_truncation"
      ? [...(expr.field_reference.path ?? []), expr.field_reference.name].join(
          ".",
        )
      : "";
export function guidedSpec(query: Query): GuidedSpec {
  const def = query.definition;
  if (
    def.kind !== "arrow" ||
    def.source.kind !== "source_reference" ||
    def.view.kind !== "segment"
  )
    throw new Error("This report uses a source query.");
  const ops = def.view.operations;
  const group = ops.find(
    (o) =>
      o.kind === "group_by" && o.field.expression.kind === "time_truncation",
  );
  const truncation =
    group?.kind === "group_by" &&
    group.field.expression.kind === "time_truncation"
      ? group.field.expression.truncation
      : "day";
  const order = ops.find((o) => o.kind === "order_by");
  const limit = ops.find((o) => o.kind === "limit");
  return {
    source: def.source.name,
    measures: ops.flatMap((o) =>
      o.kind === "aggregate" ? [expressionName(o.field.expression)] : [],
    ),
    groups: ops.flatMap((o) =>
      o.kind === "group_by" ? [expressionName(o.field.expression)] : [],
    ),
    granularity:
      truncation === "week" || truncation === "month" ? truncation : "day",
    filters: ops.flatMap((o) =>
      o.kind === "where" && o.filter.kind === "filter_string"
        ? [
            {
              field: expressionName(o.filter.expression),
              value: o.filter.filter,
            },
          ]
        : [],
    ),
    order: order?.kind === "order_by" ? order.field_reference.name : "",
    direction:
      order?.kind === "order_by" && order.direction === "asc" ? "asc" : "desc",
    limit: limit?.kind === "limit" ? limit.limit : 1000,
  };
}
export function buildGuidedQuery(
  spec: GuidedSpec,
  model: ModelInfo,
  sources: SourceDefinition[],
): Query {
  const source = sources.find((s) => s.id === spec.source);
  if (!source) throw new Error("Choose a business source.");
  if (!spec.measures.length) throw new Error("Choose at least one measure.");
  if (!Number.isInteger(spec.limit) || spec.limit < 1 || spec.limit > 1000)
    throw new Error("Row limit must be between 1 and 1,000.");
  const fields = new Map(source.fields.map((f) => [f.name, f]));
  if (
    spec.source === "cleaning_tasks" &&
    [...spec.groups, ...spec.filters.map((f) => f.field)].some((f) =>
      f.startsWith("sessions.employee_"),
    ) &&
    spec.measures.some((f) => !f.startsWith("sessions."))
  )
    throw new Error(
      "Use Cleaner sessions for employee comparisons. Task totals and quality cannot be assigned to each employee.",
    );
  for (const field of [
    ...spec.measures,
    ...spec.groups,
    ...spec.filters.map((f) => f.field),
  ])
    if (!fields.has(field)) throw new Error(`Unknown field: ${field}`);
  if (
    spec.measures.some((f) => fields.get(f)?.kind !== "measure") ||
    spec.groups.some((f) => fields.get(f)?.kind !== "dimension")
  )
    throw new Error("Choose measures for values and dimensions for grouping.");
  const ast = new ASTQuery({ model });
  ast.setSource(spec.source);
  const segment = ast.getOrAddDefaultSegment();
  for (const field of spec.groups) {
    const ref = reference(field),
      type = fields.get(field)!.type;
    if (type === "date")
      segment
        .addDateGroupBy(ref.name, ref.path ?? [], spec.granularity)
        .rename(alias(field));
    else if (type === "timestamp")
      segment
        .addTimestampGroupBy(ref.name, ref.path ?? [], spec.granularity)
        .rename(alias(field));
    else segment.addGroupBy(ref.name, ref.path, alias(field));
  }
  for (const field of spec.measures) {
    const ref = reference(field);
    segment.addAggregate(ref.name, ref.path, alias(field));
  }
  for (const filter of spec.filters) {
    if (!filter.value.trim())
      throw new Error("Enter a filter value or remove the filter.");
    if (fields.get(filter.field)?.kind !== "dimension")
      throw new Error("Filters must use dimensions.");
    const ref = reference(filter.field);
    segment.addWhere(ref.name, ref.path ?? [], filter.value);
  }
  if (spec.order)
    segment.addOrderBy(spec.order, spec.direction === "asc" ? "asc" : "desc");
  else {
    const dateGroup = spec.groups.find((name) =>
      ["date", "timestamp"].includes(fields.get(name)?.type ?? ""),
    );
    if (dateGroup) segment.addOrderBy(alias(dateGroup), "asc");
  }
  segment.setLimit(spec.limit);
  return ast.build();
}
export function guidedVisualization(
  spec: GuidedSpec,
  source: SourceDefinition,
  type: VisualizationType,
): VisualizationConfig {
  const fields = new Map(source.fields.map((f) => [f.name, f]));
  const formats = Object.fromEntries(
    spec.measures.map((f) => [alias(f), fields.get(f)?.format ?? "number"]),
  );
  const labels = Object.fromEntries(
    [...spec.groups, ...spec.measures].map((f) => [
      alias(f),
      fields.get(f)?.label ?? f,
    ]),
  );
  const mixed = new Set(Object.values(formats)).size > 1;
  return {
    type:
      mixed ||
      spec.groups.length > 1 ||
      (!spec.groups.length && spec.measures.length > 1)
        ? "table"
        : !spec.groups.length && type !== "table"
          ? "kpi"
          : type,
    x: alias(spec.groups[0] ?? ""),
    y: spec.measures.map(alias),
    format: fields.get(spec.measures[0])?.format ?? "number",
    fieldFormats: formats,
    fieldLabels: labels,
  };
}
